import React, { useState, useEffect } from 'react';
import {
  Table, Button, Input, Select, Space, Modal, Form, Tag, Popconfirm,
  message, Card, Typography, Row, Col, Switch, Alert,
  List, Avatar, Transfer, Spin
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  PlayCircleOutlined, PauseCircleOutlined, ApiOutlined,
  UserOutlined, LinkOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useActionColumn } from '../utils/tableLayout';

const { Title } = Typography;
const { Option } = Select;

function RobotSettings() {
  const actionColumn = useActionColumn(220);
  const [robots, setRobots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [defaults, setDefaults] = useState(null);
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [connectionRobot, setConnectionRobot] = useState(null);
  const [callback, setCallback] = useState(null);
  const [connectionError, setConnectionError] = useState('');
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [callbackBinding, setCallbackBinding] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [userBindingVisible, setUserBindingVisible] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [robotUsers, setRobotUsers] = useState([]);
  const [bindingLoading, setBindingLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    isActive: ''
  });
  
  const [form] = Form.useForm();
  const purpose = Form.useWatch('functionType', form);
  const { hasPermission, user } = useAuth();

  // 检查是否为超级管理员
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (hasPermission('robotSettings', 'write')) {
      api.get('/robot-settings/connection-defaults').then(response => setDefaults(response.data)).catch(() => setLoadError('接入默认配置加载失败，请刷新页面后重试')).finally(() => setDefaultsLoading(false));
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadRobots();
  }, [pagination.current, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRobots = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };
      
      const response = await api.get('/robot-settings', { params });
      setRobots(response.data.robots);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      setLoadError('机器人列表加载失败，请刷新页面后重试');
      console.error('加载机器人设置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      name: '', type: 'wechat', platform: 'wecom', functionType: 'receiver',
      isActive: true,
      worktoolRobotId: '',
      apiEndpoint: defaults?.apiEndpoint || 'https://api.worktool.ymdyes.cn',
      callbackBaseUrl: defaults?.callbackBaseUrl || ''
    });
    setSaveError('');
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.resetFields();
    form.setFieldsValue({ ...record, token: '', platform: record.platform || 'wecom', apiEndpoint: record.apiEndpoint || defaults?.apiEndpoint || 'https://api.worktool.ymdyes.cn', callbackBaseUrl: record.callbackBaseUrl || defaults?.callbackBaseUrl || '' });
    setSaveError('');
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/robot-settings/${id}`);
      message.success('删除成功');
      loadRobots();
    } catch (error) {
      console.error('删除失败:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      setSaveError('');
      const payload = { ...values, type: editingRecord?.type || 'wechat' };
      if (!payload.token?.trim()) delete payload.token;
      const response = editingRecord ? await api.put(`/robot-settings/${editingRecord._id}`, payload) : await api.post('/robot-settings', payload);
      message.success(editingRecord ? '更新成功' : values.functionType === 'sender' ? '发送机器人已创建，可在推送反馈中选择' : '创建成功，请继续配置回调');
      setModalVisible(false);
      loadRobots();
      if (response.data.robot?.type === 'wechat' && response.data.robot.functionType !== 'sender') await handleConnection(response.data.robot);
    } catch (error) {
      setSaveError(error.response?.data?.errors?.map(item => item.msg).join('；') || error.response?.data?.message || '保存失败，请重试');
    } finally { setSaving(false); }
  };

  const handleConnection = async record => {
    setConnectionRobot(record); setCallback(null); setTestResult(null); setConnectionError(''); setConnectionLoading(true);
    try { setCallback((await api.get(`/robot-settings/${record._id}/callback`)).data); }
    catch (error) { setConnectionError(error.response?.data?.message || '回调地址加载失败，请重试'); }
    finally { setConnectionLoading(false); }
  };

  const handleBindCallback = async () => {
    setCallbackBinding(true); setConnectionError('');
    try {
      const response = await api.post(`/robot-settings/${connectionRobot._id}/callback/bind`);
      setCallback(response.data); message.success(response.data.message);
    } catch (error) { setConnectionError(error.response?.data?.message || '回调绑定失败，请重试'); }
    finally { setCallbackBinding(false); }
  };

  const handleToggleActive = async (record) => {
    try {
      await api.patch(`/robot-settings/${record._id}/toggle`);
      message.success(`机器人已${record.isActive ? '禁用' : '启用'}`);
      loadRobots();
    } catch (error) {
      console.error('切换状态失败:', error);
    }
  };

  const handleTestConnection = async (record) => {
    setTestingId(record._id); setTestResult(null); setConnectionError('');
    try {
      const response = await api.post(`/robot-settings/${record._id}/test`);
      setTestResult(response.data);
      loadRobots();
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.message || '连接检测失败，请重试' });
    } finally { setTestingId(null); }
  };

  // 用户绑定相关函数
  const handleUserBinding = async (record) => {
    setSelectedRobot(record);
    setUserBindingVisible(true);
    await loadUsersAndBindings(record);
  };

  const loadUsersAndBindings = async (robot) => {
    try {
      setBindingLoading(true);
      
      // 加载所有用户
      const usersResponse = await api.get('/users', { 
        params: { limit: 1000 } // 获取所有用户
      });
      setAllUsers(usersResponse.data.users);

      // 加载已绑定的用户（使用新的绑定API）
      const bindingsResponse = await api.get(`/user-robot-bindings/robot/${robot.worktoolRobotId}/users`);
      setRobotUsers(bindingsResponse.data.data || []);
      
    } catch (error) {
      console.error('加载用户和绑定信息失败:', error);
      message.error('加载用户信息失败');
    } finally {
      setBindingLoading(false);
    }
  };

  const handleBindingChange = async (targetKeys, direction, moveKeys) => {
    try {
      setBindingLoading(true);
      
      if (direction === 'right') {
        // 添加绑定（使用新的绑定API）
        const userIds = moveKeys;
        for (const userId of userIds) {
          await api.post('/user-robot-bindings/bind', {
            userId,
            receiverRobotId: selectedRobot._id,
            permissions: {
              canRead: true,
              canWrite: false,
              canDelete: false,
              canManageSettings: false
            }
          });
        }
        message.success(`成功绑定 ${userIds.length} 个用户`);
      } else {
        // 删除绑定
        for (const userId of moveKeys) {
          const binding = robotUsers.find(ru => ru.user._id === userId);
          if (binding) {
            await api.delete(`/user-robot-bindings/${binding.binding.id}`);
          }
        }
        message.success(`成功解绑 ${moveKeys.length} 个用户`);
      }
      
      // 重新加载绑定信息
      await loadUsersAndBindings(selectedRobot);
      
    } catch (error) {
      console.error('绑定操作失败:', error);
      message.error('绑定操作失败');
    } finally {
      setBindingLoading(false);
    }
  };

  const columns = [
    {
      title: '机器人名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      ellipsis: true
    },
    {
      title: 'WorkTool ID',
      dataIndex: 'worktoolRobotId',
      key: 'worktoolRobotId',
      ellipsis: true,
      width: 150,
      render: (text) => (
        <Typography.Text code copyable style={{ fontSize: '12px' }}>
          {text}
        </Typography.Text>
      )
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type, record) => {
        const colors = {
          telegram: 'blue',
          wechat: 'green',
          qq: 'orange',
          discord: 'purple',
          other: 'default'
        };
        return <Tag color={colors[type]}>{type === 'wechat' ? record.platform === 'wechat' ? '个人微信' : '企业微信' : type.toUpperCase()}</Tag>;
      }
    },
    {
      title: '功能类型',
      dataIndex: 'functionType',
      key: 'functionType',
      width: 130,
      render: (functionType) => {
        const typeConfig = {
          receiver: { color: 'blue', text: '接收消息' },
          sender: { color: 'green', text: '仅发送' },
          both: { color: 'purple', text: '接收和发送' }
        };
        const config = typeConfig[functionType] || { color: 'default', text: '未设置' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '设备状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status, record) => {
        if (!record.connectionTest?.checkedAt) return <Tag>未检测</Tag>;
        if (record.connectionTest.success && record.connectionTest.online == null) return <Tag>状态未知</Tag>;
        const colors = {
          online: 'green',
          offline: 'default',
          error: 'red',
          maintenance: 'orange'
        };
        const labels = {
          online: '在线',
          offline: '离线',
          error: '错误',
          maintenance: '维护中'
        };
        return <Tag color={colors[status]}>{labels[status]}</Tag>;
      }
    },
    {
      title: '启用状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '已启用' : '已禁用'}
        </Tag>
      )
    },
    {
      title: '统计信息',
      key: 'statistics',
      width: 110,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>消息: {record.statistics?.totalMessages || 0}</div>
          <div>用户: {record.statistics?.totalUsers || 0}</div>
          <div>群组: {record.statistics?.totalGroups || 0}</div>
        </div>
      )
    },
    {
      title: '最近消息接收',
      dataIndex: ['statistics', 'lastActiveTime'],
      key: 'lastActiveTime',
      width: 170,
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '尚未收到消息'
    },
    {
      title: '操作',
      key: 'action',
      ...actionColumn,
      render: (_, record) => (
        <Space wrap>
          {hasPermission('robotSettings', 'write') && (
            <>
              {record.type === 'wechat' && <Button
                type="link"
                icon={<ApiOutlined />}
                onClick={() => handleConnection(record)}
              >
                接入配置
              </Button>}
              <Button
                type="link"
                icon={record.isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={() => handleToggleActive(record)}
              >
                {record.isActive ? '禁用' : '启用'}
              </Button>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                编辑
              </Button>
            </>
          )}
          {isSuperAdmin && (
            <Button
              type="link"
              icon={<UserOutlined />}
              onClick={() => handleUserBinding(record)}
            >
              用户绑定
            </Button>
          )}
          {hasPermission('robotSettings', 'write') && (
            <Popconfirm
              title="确定删除这个机器人设置吗？"
              onConfirm={() => handleDelete(record._id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2}>机器人管理</Title>
        <div className="description">管理 WorkTool 接收与发送机器人，分别用于报名采集和推送反馈。</div>
      </div>

      <Alert type="info" showIcon message="添加机器人 → 绑定 QA 回调 → 开启手机接收 → 配置允许的群"
        description="接收机器人保存后显示回调 URL；发送机器人无需回调，可在推送记录 → 推送反馈中选择。报名提交使用伙伴通接口。" style={{ marginBottom: 16 }} />
      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}

      <Card>
        <div className="table-toolbar">
          <div className="left">
            <Input.Search
              placeholder="搜索机器人名称"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
            <Select
              placeholder="选择类型"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => handleFilterChange('type', value)}
            >
              <Option value="telegram">Telegram</Option>
              <Option value="wechat">微信</Option>
              <Option value="qq">QQ</Option>
              <Option value="discord">Discord</Option>
              <Option value="other">其他</Option>
            </Select>
            <Select
              placeholder="选择状态"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="online">在线</Option>
              <Option value="offline">离线</Option>
              <Option value="error">错误</Option>
              <Option value="maintenance">维护中</Option>
            </Select>
            <Select
              placeholder="启用状态"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => handleFilterChange('isActive', value)}
            >
              <Option value="true">已启用</Option>
              <Option value="false">已禁用</Option>
            </Select>
            <Select
              placeholder="功能类型"
              allowClear
              style={{ width: 140 }}
              onChange={(value) => handleFilterChange('functionType', value)}
            >
              <Option value="receiver">接收消息</Option>
              <Option value="sender">仅发送</Option>
              <Option value="both">接收和发送</Option>
            </Select>
          </div>
          <div className="right">
            {hasPermission('robotSettings', 'write') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                loading={defaultsLoading}
                disabled={defaultsLoading}
              >
                添加机器人
              </Button>
            )}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={robots}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1280 }}
          tableLayout="fixed"
        />
      </Card>

      <Modal title={editingRecord ? '编辑机器人' : '添加 WorkTool 机器人'} open={modalVisible}
        onCancel={() => !saving && setModalVisible(false)} footer={null} width={640}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={saving}>
          {saveError && <Alert type="error" showIcon role="alert" message={saveError} style={{ marginBottom: 16 }} />}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="name" label="机器人名称" rules={[{ required: true, whitespace: true, message: '请输入机器人名称' }]}>
                <Input placeholder="例如：福州报名接收号" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="platform" label="微信客户端" rules={[{ required: true, message: '请选择微信客户端' }]}>
                <Select options={[{ value: 'wecom', label: '企业微信（WorkTool）' }, { value: 'wechat', label: '个人微信（WechatTool）' }]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="worktoolRobotId" label="WorkTool robotId"
            rules={[{ required: true, whitespace: true, message: '请输入手机端的 robotId' }, { min: 3, max: 100, message: 'robotId 长度须为 3–100 个字符' }]}
            extra="从对应的 WorkTool / WechatTool 手机端复制 robotId，须与手机端完全一致。">
            <Input placeholder="粘贴手机端 robotId" autoComplete="off" />
          </Form.Item>
          <Form.Item name="apiEndpoint" label="WorkTool API 地址" rules={[{ required: true, message: '请填写 WorkTool API 地址' }, { type: 'url', message: '请填写有效的 HTTP(S) 地址' }]}
            extra="官方服务使用默认地址；私有部署时改成你的 WorkTool 服务地址。">
            <Input placeholder="https://api.worktool.ymdyes.cn" />
          </Form.Item>
          <Form.Item name="token" label="WorkTool API Key（选填）" extra="仅在你的 WorkTool 服务要求 Key 时填写，与伙伴通查询和推送 Key 分别配置。编辑时留空保留原 Key。">
            <Input.Password autoComplete="new-password" placeholder={editingRecord?.hasToken ? '已配置，留空保留' : '服务未要求 Key 时留空'} maxLength={500} />
          </Form.Item>
          {purpose !== 'sender' && <Form.Item name="callbackBaseUrl" label="本系统对外地址" rules={[{ required: true, message: '请填写本系统对外地址' }, { type: 'url', message: '请填写有效的 HTTP(S) 地址' }]}
            extra="填写指向本系统后端（默认 8000 端口）的公网域名或内网穿透地址。localhost 和前端 3000 端口不能用于外部回调。保存后自动生成完整回调 URL。">
            <Input placeholder="https://你的机器人后台域名" />
          </Form.Item>}
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item name="functionType" label="机器人用途" extra="接收用于采集报名，发送用于推送反馈；也可使用同一台双向机器人。">
                <Select options={[{ value: 'receiver', label: '接收消息' }, { value: 'both', label: '接收和发送' }, { value: 'sender', label: '仅发送（推送反馈）' }]} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="isActive" label={purpose === 'sender' ? '启用发送' : '启用接收'} valuePropName="checked"><Switch /></Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>{purpose === 'sender' ? '保存发送机器人' : '保存并查看回调'}</Button>
            <Button disabled={saving} onClick={() => setModalVisible(false)}>取消</Button>
          </Space>
        </Form>
      </Modal>

      <Modal title={`接入配置 · ${connectionRobot?.name || ''}`} open={Boolean(connectionRobot)} width={680}
        onCancel={() => !callbackBinding && setConnectionRobot(null)} footer={<Button onClick={() => setConnectionRobot(null)} disabled={callbackBinding}>关闭</Button>}>
        <Spin spinning={connectionLoading}>
          {connectionError && <Alert type="error" showIcon role="alert" message={connectionError} style={{ marginBottom: 16 }} />}
          {callback && <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Typography.Text strong>1. 绑定 QA 消息回调</Typography.Text>
              <Typography.Paragraph type="secondary">将下方地址填到 WorkTool 的“机器人消息回调配置”，或点击“绑定到 WorkTool”。绑定会把该 robotId 的 QA 回调改为本系统。</Typography.Paragraph>
              <Typography.Paragraph copyable={{ text: callback.url }} style={{ overflowWrap: 'anywhere', marginBottom: 8 }}><Typography.Text code>{callback.url}</Typography.Text></Typography.Paragraph>
              {callback.isLocal && <Alert type="warning" showIcon message="当前地址仅本机可访问，WorkTool 无法回调。请先修改本系统对外地址。" style={{ marginBottom: 12 }} />}
              {!callback.enabled && <Alert type="warning" showIcon message="机器人已停用，请先编辑并启用接收。" style={{ marginBottom: 12 }} />}
              {!callback.receivesMessages && <Alert type="warning" showIcon message="该机器人仅用于发送，请编辑用途为接收消息。" style={{ marginBottom: 12 }} />}
              <Space wrap>
                <Button type="primary" loading={callbackBinding} disabled={callback.isLocal || !callback.enabled || !callback.receivesMessages} onClick={handleBindCallback}>绑定到 WorkTool</Button>
                <Button disabled={callbackBinding} onClick={() => { const robot = connectionRobot; setConnectionRobot(null); handleEdit(robot); }}>修改接入配置</Button>
                <Button onClick={() => handleConnection(connectionRobot)} disabled={callbackBinding}>刷新接收状态</Button>
              </Space>
              <div style={{ marginTop: 12 }}>
                <Typography.Text type="secondary">回调配置：{callback.lastBoundAt && callback.boundUrl === callback.url ? `已提交给 WorkTool（${new Date(callback.lastBoundAt).toLocaleString('zh-CN')}）` : '尚未通过本系统绑定；也可在 WorkTool 后台手动配置'}</Typography.Text>
              </div>
            </div>
            <div>
              <Typography.Text strong>2. 打开手机端接收开关并检查连接</Typography.Text>
              <Typography.Paragraph type="secondary">在对应的 WorkTool / WechatTool 手机端开启主功能和“新消息接收”，保持微信运行。API 连接成功后，还需发消息确认回调能够到达。</Typography.Paragraph>
              <Button icon={<ApiOutlined />} loading={testingId === connectionRobot?._id} onClick={() => handleTestConnection(connectionRobot)}>检查 API 与在线状态</Button>
              {testResult && <Alert type={testResult.success ? testResult.online === true ? 'success' : 'warning' : 'error'} showIcon role="status" message={testResult.message}
                description={testResult.responseTime != null ? `响应时间：${testResult.responseTime} ms` : undefined} style={{ marginTop: 12 }} />}
            </div>
            <div>
              <Typography.Text strong>3. 配置允许处理的群并验证消息</Typography.Text>
              <Typography.Paragraph type="secondary">在<a href="/wechat-records/groups">报名处理规则</a>中添加完整来源群名，选择此接收机器人并设置 AI 提取触发词。随后发送满足触发条件的报名消息，在<a href="/wechat-records">微信消息</a>查看接收和处理结果。</Typography.Paragraph>
              <Typography.Text>最近消息接收：{callback.lastReceivedAt ? new Date(callback.lastReceivedAt).toLocaleString('zh-CN') : '尚未收到消息'}</Typography.Text>
            </div>
            <Typography.Link href={connectionRobot?.platform === 'wechat' ? 'https://s.apifox.cn/fd79c528-852d-4caf-9db1-1d4522989e44/llms.txt' : 'https://worktool.apifox.cn/doc-861677'} target="_blank" rel="noreferrer">查看对应客户端文档</Typography.Link>
          </Space>}
        </Spin>
      </Modal>

      {/* 用户绑定模态框 */}
      <Modal
        title={
          <div>
            <LinkOutlined style={{ marginRight: 8 }} />
            用户绑定管理 - {selectedRobot?.name}
          </div>
        }
        open={userBindingVisible}
        onCancel={() => setUserBindingVisible(false)}
        footer={[
          <Button key="close" onClick={() => setUserBindingVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            左侧为未绑定用户，右侧为已绑定用户。选择用户后点击箭头进行绑定或解绑操作。
          </Typography.Text>
        </div>
        
        {bindingLoading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Transfer
            className="robot-user-transfer"
            dataSource={allUsers.map(user => ({
              key: user._id,
              title: `${user.username} (${user.email})`,
              description: `角色: ${user.role}`,
              disabled: false
            }))}
            targetKeys={robotUsers.map(ru => ru.user._id)}
            onChange={handleBindingChange}
            render={item => (
              <div>
                <div style={{ fontWeight: 500 }}>{item.title}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{item.description}</div>
              </div>
            )}
            titles={['未绑定用户', '已绑定用户']}
            showSearch
            searchPlaceholder="搜索用户"
            listStyle={{
              height: 400,
            }}
            operations={['绑定', '解绑']}
            locale={{
              itemUnit: '个用户',
              itemsUnit: '个用户',
              searchPlaceholder: '搜索用户',
              notFoundContent: '暂无数据'
            }}
          />
        )}
        
        {robotUsers.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Typography.Title level={5}>已绑定用户权限信息：</Typography.Title>
            <List
              size="small"
              dataSource={robotUsers}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={`${item.user.username} (${item.user.email})`}
                    description={
                      <div>
                        <Tag color={item.user.role === 'super_admin' ? 'red' : item.user.role === 'admin' ? 'orange' : 'blue'}>
                          {item.user.role}
                        </Tag>
                        <Space>
                          {item.association.permissions.canRead && <Tag color="green">读取</Tag>}
                          {item.association.permissions.canWrite && <Tag color="blue">写入</Tag>}
                          {item.association.permissions.canDelete && <Tag color="red">删除</Tag>}
                          {item.association.permissions.canManageSettings && <Tag color="purple">管理设置</Tag>}
                        </Space>
                      </div>
                    }
                  />
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    绑定时间: {new Date(item.association.createdAt).toLocaleString('zh-CN')}
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default RobotSettings;
